import { useState } from 'react'
import { api, type AccountErpView } from '../lib/api.ts'
import { withSavedAttachments, type ErpConnectionFields } from '../lib/attachmentsDatabase.ts'
import { ERP_CONNECTION_SOURCE_LABEL } from '../lib/vocabulary.ts'
import { Button } from './Button.tsx'
import { Checkbox } from './Checkbox.tsx'
import { useConfirm } from './Confirm.tsx'
import { EntityForm, Field, FieldGroup } from './EntityForm.tsx'
import { Input } from './Input.tsx'
import { useToast } from './Toast.tsx'

/**
 * How this account reaches its Formula Certa (#639), on the account's
 * Conexão tab.
 *
 * The password is typed and never shown: the server sends back only whether
 * one is saved, so an empty password field means "keep it". "Testar" tries
 * the form as typed, before anything is saved -- a wrong host saved first
 * would be what every activity of this account dials next.
 *
 * The attachments database is not among these fields since #689: it is the
 * Receitas section's, beside the Drive folder it is the other choice to. It
 * is still saved in this connection's row, so a save here carries the one the
 * account has now.
 */
export function AccountErpConnection({
  accountId,
  view,
  onSaved,
}: {
  accountId: string
  view: AccountErpView
  onSaved: (next: AccountErpView) => void
}) {
  const [form, setForm] = useState<ErpConnectionFields>(() => fieldsOf(view))
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  const set = <K extends keyof ErpConnectionFields>(key: K, value: ErpConnectionFields[K]) =>
    setForm({ ...form, [key]: value })
  const dirty = JSON.stringify(form) !== JSON.stringify(fieldsOf(view))

  function take(next: AccountErpView) {
    onSaved(next)
    setForm(fieldsOf(next))
  }

  async function save() {
    setSaving(true)
    try {
      take(await api.saveAccountErp(accountId, withSavedAttachments(form, view)))
      toast.show('Conexão com o ERP salva', { tone: 'success' })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function test() {
    setTesting(true)
    try {
      // Unchanged and saved: test what the account uses now, fallback included.
      const result = await api.testAccountErp(
        accountId,
        dirty ? withSavedAttachments(form, view) : undefined,
      )
      if (result.ok) toast.show(`ERP respondeu em ${result.ms} ms`, { tone: 'success' })
      else toast.show(`ERP não respondeu: ${result.error ?? 'falhou'}`, { tone: 'error' })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao testar', { tone: 'error' })
    } finally {
      setTesting(false)
    }
  }

  async function remove() {
    const ok = await confirm({
      title: 'Remover a conexão com o ERP?',
      message: removeWarning(view),
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!ok) return
    try {
      take(await api.removeAccountErp(accountId))
      toast.show('Conexão removida', { tone: 'success' })
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao remover', { tone: 'error' })
    }
  }

  const saved = view.connection
  return (
    <EntityForm
      title="Conexão com o ERP"
      description={ERP_CONNECTION_SOURCE_LABEL[view.source] ?? view.source}
      onSave={view.canSave ? () => void save() : undefined}
      saving={saving}
      dirty={dirty}
    >
      {!view.enabled && (
        <p className="text-tSmall text-text-weak md:col-span-2">
          A integração com o ERP está desligada neste ambiente (ERP_INTEGRATION_ENABLED): as
          conferências não leem nada, com ou sem conexão salva. O teste funciona mesmo assim.
        </p>
      )}
      {!view.canSave && (
        <p className="text-tSmall text-text-error md:col-span-2">
          ERP_CREDENTIALS_KEY não está configurada: não dá para guardar a senha.
        </p>
      )}
      <Field label="Host" value={form.host} editable onChange={(v) => set('host', v)} />
      <Field
        label="Porta"
        value={String(form.port)}
        editable
        onChange={(v) => set('port', Number(v.replace(/\D/g, '')) || 0)}
      />
      <Field
        label="Banco principal"
        value={form.database}
        editable
        onChange={(v) => set('database', v)}
        helperText="Caminho ou alias do banco, como o servidor do ERP o expõe."
      />
      <Field label="Usuário" value={form.user} editable onChange={(v) => set('user', v)} />
      <Input
        label="Senha"
        type="password"
        autoComplete="new-password"
        value={form.password}
        onChange={(event) => set('password', event.target.value)}
        helperText={
          saved?.passwordSet === true
            ? 'Há uma senha salva. Em branco, ela é mantida — a menos que o host, a porta, o banco, o usuário, o auth plugin ou o wire crypt mudem.'
            : 'Obrigatória no primeiro cadastro.'
        }
      />
      <Field label="Charset" value={form.charset} editable onChange={(v) => set('charset', v)} />
      <Field
        label="Auth plugin"
        value={form.authPlugin}
        editable
        onChange={(v) => set('authPlugin', v)}
      />
      <Field
        label="Timeout (ms)"
        value={String(form.statementTimeoutMs)}
        editable
        onChange={(v) => set('statementTimeoutMs', Number(v.replace(/\D/g, '')) || 0)}
        helperText="Limite para abrir a conexão e para cada consulta."
      />
      <FieldGroup label="Wire crypt" wide={false}>
        <Checkbox
          label="Cifrar a conexão (só se o servidor oferece)"
          checked={form.wireCrypt}
          onChange={(event) => set('wireCrypt', event.target.checked)}
        />
      </FieldGroup>
      {saved !== null && (
        <Field
          label="Salva por"
          value={`${saved.setBy} em ${new Date(saved.setAt).toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
          })}`}
        />
      )}
      <div className="flex flex-wrap gap-2 md:col-span-2">
        <Button
          type="button"
          variant="secondary"
          tone="neutral"
          size="small"
          disabled={testing}
          onClick={() => void test()}
        >
          {testing ? 'Testando…' : 'Testar conexão'}
        </Button>
        {saved !== null && (
          <Button
            type="button"
            variant="tertiary"
            tone="danger"
            size="small"
            onClick={() => void remove()}
          >
            Remover conexão
          </Button>
        )}
      </div>
    </EntityForm>
  )
}

/** The section's fields as the saved connection fills them, or the defaults when there is none. */
function fieldsOf(view: AccountErpView): ErpConnectionFields {
  const saved = view.connection
  return {
    host: saved?.host ?? '',
    port: saved?.port ?? view.defaults.port,
    database: saved?.database ?? '',
    user: saved?.user ?? '',
    password: '',
    charset: saved?.charset ?? view.defaults.charset,
    authPlugin: saved?.authPlugin ?? view.defaults.authPlugin,
    wireCrypt: saved?.wireCrypt ?? view.defaults.wireCrypt,
    statementTimeoutMs: saved?.statementTimeoutMs ?? view.defaults.statementTimeoutMs,
  }
}

/** What removing takes with it: the attachments database lives in the same row. */
export function removeWarning(view: AccountErpView): string {
  const attachments =
    (view.connection?.attachmentsDatabase ?? '') === '' ? '' : ', e o banco de anexos com ela'
  return `Esta conta para de ler o ERP por esta conexão${attachments}. Se ela for a conta das variáveis ERP_*, volta a usá-las.`
}
